/**
 * lib/supabase/client.ts
 * Cliente de Supabase. Single source of truth para toda la app.
 */

import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string
const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!url || !key) {
  throw new Error('[Supabase] Faltan VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en .env.local')
}

/**
 * Cliente principal (anon key). RLS protege los datos.
 * Importar en toda la app: import { supabase } from '@lib/supabase/client'
 */
export const supabase = createClient(url, key, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'celmaxx-auth-v1',
  },
})

/** JWT de la sesión activa, o null si no hay sesión */
export async function getJWT(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Llama a una Edge Function con autenticación automática.
 * Lanza error con .code y .statusCode si el servidor responde error.
 */
export async function callEdgeFunction<T = unknown>(
  fn: string,
  body: Record<string, unknown>
): Promise<T> {
  const jwt = await getJWT()
  if (!jwt) throw Object.assign(new Error('Sin sesión activa'), { code: 'NO_SESSION' })

  const res = await fetch(`${url}/functions/v1/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify(body),
  })

  // Parsear body — puede ser texto plano cuando la función no existe (404 "Function not found")
  let data: { data?: T; error?: { code: string; message: string } }
  try {
    data = (await res.json()) as { data?: T; error?: { code: string; message: string } }
  } catch {
    if (res.status === 404) {
      throw Object.assign(
        new Error(
          `La función "${fn}" no está desplegada. Ejecuta: supabase functions deploy ${fn}`
        ),
        { code: 'FUNCTION_NOT_FOUND', statusCode: 404 }
      )
    }
    throw Object.assign(new Error(`Error ${res.status} — respuesta inesperada del servidor`), {
      code: 'INVALID_RESPONSE',
      statusCode: res.status,
    })
  }

  if (!res.ok || data.error) {
    throw Object.assign(new Error(data.error?.message ?? `Error ${res.status}`), {
      code: data.error?.code ?? 'HTTP_ERROR',
      statusCode: res.status,
    })
  }

  return data.data as T
}
